import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { AddCustomerModal } from '../components/domain/AddCustomerModal.js';
import { UploadModal } from '../components/domain/UploadModal.js';
import { downloadCSV } from '../lib/csv.js';
import { useResetSub } from '../features/filters/useFilters.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';

interface Contact {
  id: string;
  name: string;
  role: string | null;
  phone: string | null;
  email: string | null;
}

interface ProjectRef {
  id: string;
  code: string;
  name: string;
  status: string;
  address?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface Customer {
  id: string;
  name: string;
  type: string;
  sector: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  dept?: string[];
  projects: ProjectRef[];
  contacts: Contact[];
}

interface Worker {
  id: string;
  name: string;
  role: string;
  status: string;
  agencyCode?: string | null;
  photo?: string | null;
  projects: string[];
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const AVC = ['#E8A427', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#10B981', '#EC4899', '#14B8A6'];
function avc(name: string): string {
  return AVC[name.charCodeAt(0) % AVC.length] ?? AVC[0]!;
}

function avEl(name: string, size = 38, font = 13, photo: string | null = null): JSX.Element {
  if (photo) {
    return (
      <div className="av av-photo" style={{ width: size, height: size }}>
        <img src={photo} alt={name} loading="lazy" />
      </div>
    );
  }
  return (
    <div className="av" style={{ width: size, height: size, fontSize: font, background: avc(name) }}>
      {initials(name)}
    </div>
  );
}

function chp(project: ProjectRef): JSX.Element {
  return (
    <span key={project.id} className="pill" style={{ margin: 1 }}>
      {project.code}
    </span>
  );
}

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  MainContractor: 'Main Contractor',
  LocalAuthority: 'Local Authority',
  HousingAssociation: 'Housing Association',
  PublicSector: 'Public Sector',
};
function customerTypeLabel(t: string | null | undefined): string {
  if (!t) return '';
  return CUSTOMER_TYPE_LABELS[t] ?? t;
}

export function Customers() {
  const navigate = useNavigate();
  const { canImport, canExport, canEditCustomers } = useRolePerms();
  const [search, setSearch] = useState('');
  const [fType, setFType] = useState('');
  const [fSector, setFSector] = useState('');
  const [fProjects, setFProjects] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<typeof customers[number] | null>(null);

  useResetSub(() => { setSearch(''); setFType(''); setFSector(''); setFProjects(''); });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get<Customer[]>('/customers').then((r) => r.data),
  });
  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data),
  });

  const sectorOptions = useMemo(
    () => [...new Set(customers.map((c) => c.sector).filter(Boolean) as string[])].sort(),
    [customers],
  );

  const filtered = useMemo(() => {
    let r = [...customers];
    const s = search.toLowerCase().trim();
    if (s) r = r.filter((c) => [c.name, c.type, c.sector, c.address].filter(Boolean).join(' ').toLowerCase().includes(s));
    if (fType) r = r.filter((c) => c.type === fType);
    if (fSector) r = r.filter((c) => c.sector === fSector);
    if (fProjects === 'active') r = r.filter((c) => c.projects.some((p) => p.status === 'live'));
    if (fProjects === 'none') r = r.filter((c) => c.projects.length === 0);
    return r;
  }, [customers, search, fType, fSector, fProjects]);

  const sub = `${customers.length} companies`;
  const countLabel = `${filtered.length} compan${filtered.length !== 1 ? 'ies' : 'y'} shown`;

  const active = activeId ? customers.find((c) => c.id === activeId) : null;

  const clearFilters = () => { setSearch(''); setFType(''); setFSector(''); setFProjects(''); };

  return (
    <div className="page" id="page-customers">
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 110px)' }}>
          <div className="hbar">
            <div>
              <div className="ph">Customers</div>
              <div className="ps" id="cust-sub">{sub}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {canImport ? <button className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}>⬆ Import</button> : null}
              {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV('customers.csv', filtered as any, [
                { key: 'name', label: 'Name' },
                { key: 'type', label: 'Type' },
                { key: 'sector', label: 'Sector' },
                { key: 'phone', label: 'Phone' },
                { key: 'email', label: 'Email' },
                { key: 'address', label: 'Address' },
              ])}>⬇ Export</button> : null}
              {canEditCustomers ? <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ New Customer</button> : null}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 10, padding: '5px 8px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)', minHeight: 36 }}>
            <div className="tbl-search" style={{ minWidth: 180, flex: 1, margin: 0 }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input id="cust-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="snav-sep"></div>
            <div className="fpill">
              <span>Type:</span>
              <SearchableSelect
                id="cust-f-type"
                value={fType}
                onChange={setFType}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'Developer', label: 'Developer' },
                  { value: 'MainContractor', label: 'Main Contractor' },
                  { value: 'LocalAuthority', label: 'Local Authority' },
                  { value: 'HousingAssociation', label: 'Housing Association' },
                  { value: 'Private', label: 'Private' },
                  { value: 'Retailer', label: 'Retailer' },
                  { value: 'PublicSector', label: 'Public Sector' },
                  { value: 'Other', label: 'Other' },
                ]}
              />
            </div>
            <div className="fpill">
              <span>Sector:</span>
              <SearchableSelect
                id="cust-f-sector"
                value={fSector}
                onChange={setFSector}
                placeholder="All"
                options={[{ value: '', label: 'All' }, ...sectorOptions.map((s) => ({ value: s, label: s }))]}
              />
            </div>
            <div className="fpill">
              <span>Projects:</span>
              <select id="cust-f-projects" value={fProjects} onChange={(e) => setFProjects(e.target.value)}>
                <option value="">Any</option>
                <option value="active">Has Live</option>
                <option value="none">None</option>
              </select>
            </div>
            <div className="snav-sep"></div>
            <span id="cust-count" style={{ fontSize: 10, color: 'var(--muted)' }}>{countLabel}</span>
            <button className="btn btn-ghost btn-xs" onClick={clearFilters} title="Clear filters" style={{ marginLeft: 'auto' }}>✕ Clear</button>
          </div>
          <div className="g2" id="cust-grid">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="card"
                style={{ cursor: 'pointer', transition: 'border-color .15s' }}
                onClick={() => setActiveId(c.id)}
              >
                <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 9 }}>
                  {avEl(c.name, 38, 13)}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13 }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{customerTypeLabel(c.type)}{c.sector ? ` · ${c.sector}` : ''}</div>
                  </div>
                  <span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17 }}>
                    {c.projects.length}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 6 }}>{c.address ?? ''}</div>
                <div>
                  {c.projects.map((p) => chp(p))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          id="cust-dp"
          style={{ width: 480, minWidth: 480, background: 'var(--surface)', borderLeft: '1px solid var(--border)', overflowY: 'auto', padding: 16, flexShrink: 0, display: active ? 'block' : 'none', maxHeight: 'calc(100vh - 90px)' }}
        >
          {active ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 13 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {avEl(active.name, 42, 14)}
                  <div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14 }}>{active.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{customerTypeLabel(active.type)}{active.sector ? ` · ${active.sector}` : ''}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  {canEditCustomers ? <button className="btn btn-ghost btn-sm" onClick={() => setEditing(active)}>✏ Edit</button> : null}
                  <button className="close-btn" onClick={() => setActiveId(null)}>×</button>
                </div>
              </div>
              {active.phone ? (
                <div className="dp-row"><span className="dp-key">Phone</span><span className="dp-val"><a href={`tel:${active.phone}`} style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 12 }}>{active.phone}</a></span></div>
              ) : null}
              {active.email ? (
                <div className="dp-row"><span className="dp-key">Email</span><span className="dp-val"><a href={`mailto:${active.email}`} style={{ color: 'var(--info)', fontSize: 12 }}>{active.email}</a></span></div>
              ) : null}
              {active.address ? (
                <div className="dp-row"><span className="dp-key">Address</span><span className="dp-val" style={{ fontSize: 11, color: 'var(--muted)' }}>{active.address}</span></div>
              ) : null}
              {active.dept && active.dept.length ? (
                <div className="dp-row"><span className="dp-key">Depts</span><span className="dp-val">{active.dept.map((d) => <span key={d} className="pill" style={{ margin: 1 }}>{d}</span>)}</span></div>
              ) : null}
              <div className="divider"></div>
              <div className="sec-title">Contacts ({active.contacts.length})</div>
              {active.contacts.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>No contacts</div>
              ) : (
                active.contacts.map((ct) => (
                  <div
                    key={ct.id}
                    onClick={() => navigate(`/contacts?id=${ct.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, padding: 8, borderRadius: 8, border: '1px solid var(--border)', marginBottom: 7, cursor: 'pointer', background: 'var(--card)' }}
                  >
                    {avEl(ct.name, 28, 11)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{ct.name}</div>
                      {ct.role ? <div style={{ fontSize: 10, color: 'var(--muted)' }}>{ct.role}</div> : null}
                      {ct.email ? <div style={{ fontSize: 10, color: 'var(--info)' }}>{ct.email}</div> : null}
                    </div>
                    {ct.phone ? <span style={{ fontSize: 9, color: 'var(--muted)' }}>{ct.phone}</span> : null}
                  </div>
                ))
              )}
              <div className="divider"></div>
              <div className="sec-title">Projects ({active.projects.length})</div>
              {active.projects.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>No projects</div>
              ) : (
                active.projects.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/projects?id=${p.id}`)}
                    style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 9, padding: 11, marginBottom: 8, cursor: 'pointer', transition: 'border-color .15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                      <span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 11 }}>{p.code}</span>
                      <span className={`badge b-${p.status}`}>{p.status}</span>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{p.name}</div>
                    {p.address ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.address}</div> : null}
                    {(p.startDate || p.endDate) ? (
                      <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>
                        Start {p.startDate?.slice(0, 10) ?? '—'} → End {p.endDate?.slice(0, 10) ?? '—'}
                      </div>
                    ) : null}
                  </div>
                ))
              )}

              {(() => {
                const projectCodes = new Set(active.projects.map((p) => p.code));
                const ws = workers.filter((w) => w.projects?.some((code) => projectCodes.has(code)));
                if (ws.length === 0) return null;
                const sortedProjs = [...active.projects].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
                return (
                  <>
                    <div className="divider"></div>
                    <div className="sec-title">Workers on Client Sites ({ws.length})</div>
                    {sortedProjs.map((p) => {
                      const projWorkers = ws.filter((w) => w.projects.includes(p.code));
                      if (!projWorkers.length) return null;
                      return (
                        <div key={p.id} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: '1px solid var(--border)', marginBottom: 5 }}>
                            <span
                              onClick={(e) => { e.stopPropagation(); navigate(`/projects?id=${p.id}`); }}
                              style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 10, color: 'var(--accent)', cursor: 'pointer', letterSpacing: '.5px' }}
                            >{p.code}</span>
                            <span style={{ fontSize: 10, color: 'var(--muted)', flex: 1 }}>{p.name}</span>
                            <span className={`badge b-${p.status}`}>{p.status}</span>
                          </div>
                          {projWorkers.map((w) => (
                            <div
                              key={w.id}
                              onClick={() => navigate(`/workers?id=${w.id}`)}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px', borderRadius: 7, cursor: 'pointer', transition: 'background .1s' }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(232,164,39,.05)')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                            >
                              {avEl(w.name, 24, 9, w.photo ?? null)}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, fontWeight: 500 }}>{w.name}</div>
                                <div style={{ fontSize: 9, color: 'var(--muted)' }}>
                                  {w.role}
                                  {w.agencyCode ? <> · <span style={{ color: 'var(--info)' }}>{w.agencyCode}</span></> : null}
                                </div>
                              </div>
                              <span className={`badge b-${w.status}`}>{w.status === 'dnu' ? 'DNU' : w.status}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </>
          ) : null}
        </div>
      </div>
      {addOpen ? <AddCustomerModal onClose={() => setAddOpen(false)} /> : null}
      {editing ? <AddCustomerModal onClose={() => setEditing(null)} initial={editing as any} /> : null}
      {importOpen ? <UploadModal entity="customers" onClose={() => setImportOpen(false)} /> : null}
    </div>
  );
}
